import { Box, Group, Paper, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { cloneElement, isValidElement, type CSSProperties, type ReactElement, type ReactNode } from 'react';

interface DrawerActionCardProps {
  label: ReactNode;
  description?: ReactNode;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  style?: CSSProperties;
  iconColor?: string;
  iconVariant?: 'filled' | 'light' | 'outline' | 'transparent' | 'white' | 'default';
  iconRadius?: string | number;
  iconWrapperSize?: string | number;
  iconSize?: number;
  withChevron?: boolean;
  rightSection?: ReactNode;
  truncateDescription?: boolean;
}

const DrawerActionCard = ({
  label,
  description,
  icon,
  onClick,
  disabled = false,
  ariaLabel,
  style,
  iconColor = 'blue',
  iconVariant = 'light',
  iconRadius = 'md',
  iconWrapperSize = 'lg',
  iconSize = 20,
  withChevron = true,
  rightSection,
  truncateDescription = false,
}: DrawerActionCardProps) => {
  const buttonStyle: CSSProperties = {
    width: '100%',
    textAlign: 'left',
    ...style,
    ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {})
  };
  const resolvedAriaLabel =
    ariaLabel ?? (typeof label === 'string' ? label : undefined);
  const iconNode = isValidElement(icon)
    ? cloneElement(icon as ReactElement, { size: iconSize })
    : icon;
  const resolvedRightSection = rightSection ?? (withChevron ? <IconChevronRight size={18} /> : null);

  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      aria-label={resolvedAriaLabel}
      style={buttonStyle}>
      <Paper withBorder radius="md" p="md" shadow="xs">
        <Group wrap="nowrap" gap="md" align="center">
          <ThemeIcon variant={iconVariant} color={iconColor} size={iconWrapperSize} radius={iconRadius}>
            {iconNode}
          </ThemeIcon>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600}>{label}</Text>
            {description ? (
              <Text
                size="sm"
                c="dimmed"
                style={
                  truncateDescription
                    ? {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }
                    : undefined
                }>
                {description}
              </Text>
            ) : null}
          </Box>
          {resolvedRightSection}
        </Group>
      </Paper>
    </UnstyledButton>
  );
};

export default DrawerActionCard;
